import Vue from 'vue';
import Counties from '@/shared/utils/counties';
import ApiService from '@/shared/services/api.service';
import i18n from '@/shared/plugins/vue-i18n';
import { PERMISSIONS_LIST } from '@/security/permissionsMapping';
import Errors from '@/shared/error/errors';
import moment from 'moment';
import i18nService from '@/shared/services/i18n.service';
import { TranslationsService } from '@/shared/services/translations.service';

const SETTINGS_TRANSLATIONS_NAMESPACE = 'settings';
const ORGANISATIONS_TRANSLATIONS_NAMESPACE = 'organisations';
const APPLICATION_TYPES_TRANSLATIONS_NAMESPACE = 'application_types';
const COMPLAIN_TYPES_TRANSLATIONS_NAMESPACE = 'complain_types';
const DISCOUNTS_TRANSLATIONS_NAMESPACE = 'discounts';
const TAX_CATEGORIES_TRANSLATIONS_NAMESPACE = 'tax_categories';

const applySettingsTranslations = async (context) => {
    const language = i18nService.getActiveLanguage();
    if (language === 'ro') {
        return;
    }

    const bundle = await TranslationsService.bundle(SETTINGS_TRANSLATIONS_NAMESPACE, language);
    context.commit(APPLY_SETTINGS_TRANSLATIONS, bundle);
};

// action types
export const FETCH_SETTINGS = 'fetchSettings';
export const FETCH_PUBLIC_SETTINGS = 'fetchPublicSettings';
export const DOWNLOAD_FILE = 'downloadFile';
export const FETCH_SORTING_PARAMS = 'fetchSortingParams';
export const SET_ACTIVE_LOADING = 'setActiveLoading';
export const SET_INACTIVE_LOADING = 'setInactiveLoading';
export const GET_ORGANISATIONS_LOGO = 'getOrganisationsLogo';
export const SET_ATLAS_PUBLIC_VIEW_STATE = 'setAtlasPublicViewState';
export const DOWNLOAD_FILE_CONTENT = 'downloadFileContent';
export const FETCH_ORGANISATIONS_TRANSLATIONS = 'fetchOrganisationsTranslations';
export const FETCH_APPLICATION_TYPES_TRANSLATIONS = 'fetchApplicationTypesTranslations';
export const FETCH_COMPLAIN_TYPES_TRANSLATIONS = 'fetchComplainTypesTranslations';
export const FETCH_DISCOUNTS_TRANSLATIONS = 'fetchDiscountsTranslations';
export const FETCH_TAX_CATEGORIES_TRANSLATIONS = 'fetchTaxCategoriesTranslations';

// mutation types
export const SET_ORGANISATIONS = 'setOrganisations';
export const SET_ORGANISATION = 'setOrganisation';
export const CLEAR_ORGANISATION = 'clearOrganisation';
export const SET_USER_TYPES = 'setUserTypes';
export const SET_SETTINGS = 'setSettings';
export const SET_PUBLIC_SETTINGS = 'setPublicSettings';
export const APPLY_SETTINGS_TRANSLATIONS = 'applySettingsTranslations';
export const SET_SORTING_PARAMS = 'setSortingParams';
export const SET_LOGO_TOKEN = 'setLogoToken';
export const SET_MODULE_NAME = 'setModuleName';
export const SET_ATLAS_PUBLIC_VIEW = 'setAtlasPublicView';
export const SET_ORGANISATIONS_TRANSLATIONS = 'setOrganisationsTranslations';
export const SET_APPLICATION_TYPES_TRANSLATIONS = 'setApplicationTypesTranslations';
export const SET_COMPLAIN_TYPES_TRANSLATIONS = 'setComplainTypesTranslations';
export const SET_DISCOUNTS_TRANSLATIONS = 'setDiscountsTranslations';
export const SET_TAX_CATEGORIES_TRANSLATIONS = 'setTaxCategoriesTranslations';

const state = {
    loading: {},
    counties: Counties,
    settings: {},
    publicSettings: {},
    defaultAvatar: process.env.BASE_URL + 'media/users/default.jpg',
    statuses: [
        {
            text: i18n.t('GENERAL.ACTIVE'),
            value: 'active',
        },
        {
            text: i18n.t('GENERAL.INACTIVE'),
            value: 'inactive',
        },
    ],
    organisations: [],
    organisation: {},
    organisationsTranslations: {},
    applicationTypesTranslations: {},
    complainTypesTranslations: {},
    discountsTranslations: {},
    taxCategoriesTranslations: {},
    permissions: PERMISSIONS_LIST,
    logoToken: null,
    moduleName: null,
    isAtlasPublicView: false,
    registrationBlacklistedDomains: ['cjcluj.ro']
};

const getters = {
    appConfig: (state) => state.settings,
    publicAppConfig: (state) => state.publicSettings,
    loading: (state) => state.loading,
    statuses: (state) => state.statuses,
    defaultCounty: (state) => {
        return state.settings.DEFAULT_COUNTY;
    },
    defaultCountyId: (state) => {
      const defaultCounty = state.settings.DEFAULT_COUNTY;
      return state.counties.find((el) => el.county === defaultCounty)?.id;
    },
    defaultCountyUatId: (state) => {
        const defaultCounty = state.settings.DEFAULT_COUNTY;
        return state.counties.find((el) => el.county === defaultCounty)?.uat_id;
    },
    registrationBlacklistedDomains: (state) => {
      return state.registrationBlacklistedDomains;
    },
    counties: (state) => state.counties,
    permissions: (state) => state.permissions,
    organisations: (state) => state.organisations,
    organisation: (state) => state.organisation,
    translatedOrganisationName: (state) => (id, sourceName) =>
        state.organisationsTranslations[id]?.name || sourceName,
    translatedOrganisationDescription: (state) => (id, sourceDescription) =>
        state.organisationsTranslations[id]?.description || sourceDescription,
    translatedApplicationTypeName: (state) => (id, sourceName) =>
        state.applicationTypesTranslations[id]?.name || sourceName,
    translatedComplainTypeName: (state) => (id, sourceName) =>
        state.complainTypesTranslations[id]?.name || sourceName,
    translatedDiscountName: (state) => (id, sourceName) =>
        state.discountsTranslations[id]?.name || sourceName,
    translatedDiscountDescription: (state) => (id, sourceDescription) =>
        state.discountsTranslations[id]?.description || sourceDescription,
    translatedTaxDescription: (state) => (tax) => {
        if (tax?.application_type_id) {
            return state.applicationTypesTranslations[tax.application_type_id]?.name || tax.description;
        }
        if (tax?.tax_category_id) {
            return state.taxCategoriesTranslations[tax.tax_category_id]?.name || tax.description;
        }
        return tax?.description;
    },
    currentDate: (state) => moment(new Date()).format(state.settings.DATE_FORMAT),
    defaultAvatar: (state) => state.defaultAvatar,
    logoToken: (state) => state.logoToken,
    moduleName: (state) => state.moduleName,
    isAtlasPublicView: (state) => state.isAtlasPublicView,
};

const actions = {
    getOrganisations(context) {
        context.commit('shared/activateLoading', 'shared/getOrganisations', { root: true });
        return new Promise(resolve => {
            // TODO: at some point 100 will not be enough
            ApiService.get('organisations?limit=100').then(({data}) => {
                resolve(data);
                context.commit(SET_ORGANISATIONS, data.data);
            }) .catch(({ error }) => {
                reject(error);
            })
            .finally(() => {
                context.commit('shared/deactivateLoading', 'shared/getOrganisations', { root: true });
            });
        });
    },
    getOrganisation(context, id) {
        context.commit('shared/activateLoading', 'shared/getOrganisation', { root: true });
        return new Promise((resolve, reject) => {
            ApiService.get(`organisations/${id}`)
            .then(({ data }) => {
                context.commit(SET_ORGANISATION, data);
                resolve(data);
            })
            .catch((error) => {
                Errors.handle(error);
            })
            .finally(() => {
                context.commit('shared/deactivateLoading', 'shared/getOrganisation', { root: true });
            })
        });
    },
    getUserTypes(context) {
        context.commit('shared/activateLoading', 'shared/getUserTypes', { root: true });
        return new Promise((resolve, reject) => {
            ApiService.get('user-types').then(({data}) => {
                resolve(data);
                context.commit(SET_USER_TYPES, data.data);
            }).catch(({ error }) => {
                reject(error);
            })
            .finally(() => {
                context.commit('shared/deactivateLoading', 'shared/getUserTypes', { root: true });
            });
        });
    },
    async [FETCH_SETTINGS](context) {
        const maxRetries = 5; // Maximum number of retries
        const retryDelay = 1000; // Delay between retries in milliseconds
        let retries = 0;

        const fetchSettings = () => {
            context.commit('shared/activateLoading', 'shared/fetchSettings', { root: true });

            // TODO: at some point 100 will not be enough
            return ApiService.get('settings-registry?limit=100')
                .then(async ({ data }) => {
                    context.commit(SET_SETTINGS, data.data);
                    await applySettingsTranslations(context);
                    context.commit('shared/deactivateLoading', 'shared/fetchSettings', { root: true });
                    return Promise.resolve(data.data);
                })
                .catch((error) => {
                    if (error.response.status === 403) {
                        return;
                    }
                    if (retries < maxRetries) {
                        retries++;
                        return new Promise((resolve, reject) => {
                            setTimeout(() => {
                                resolve(fetchSettings()); // Retry the request after delay
                            }, retryDelay);
                        });
                    } else {
                        context.commit('shared/deactivateLoading', 'shared/fetchSettings', { root: true });
                        return Promise.reject(error); // If max retries reached, reject with the error
                    }
                });
        };

        return fetchSettings();
    },
    async [FETCH_PUBLIC_SETTINGS](context) {
        const maxRetries = 5; // Maximum number of retries
        const retryDelay = 1000; // Delay between retries in milliseconds
        let retries = 0;

        const fetchPublicSettings = () => {
            context.commit('shared/activateLoading', 'shared/fetchPublicSettings', { root: true });

            // TODO: at some point 100 will not be enough
            return ApiService.getFrom('settings-registry/public?limit=100', window.VUE_APP_API_URL)
                .then(async ({ data }) => {
                    context.commit(SET_PUBLIC_SETTINGS, data.data);
                    await applySettingsTranslations(context);
                    context.commit('shared/deactivateLoading', 'shared/fetchPublicSettings', { root: true });
                    return Promise.resolve(data.data);
                })
                .catch((error) => {
                    if (error.response.status === 403) {
                        return;
                    }
                    if (retries < maxRetries) {
                        retries++;
                        return new Promise((resolve, reject) => {
                            setTimeout(() => {
                                resolve(fetchPublicSettings()); // Retry the request after delay
                            }, retryDelay);
                        });
                    } else {
                        context.commit('shared/deactivateLoading', 'shared/fetchPublicSettings', { root: true });
                        return Promise.reject(error); // If max retries reached, reject with the error
                    }
                });
        };

        return fetchPublicSettings();
    },    
    [DOWNLOAD_FILE](context, { url, fileName, token }) {
        context.commit('shared/activateLoading', 'shared/downloadFile', { root: true });
        return new Promise((resolve, reject) => {
            ApiService.download(url, token).then((response) => {
                const fileURL = window.URL.createObjectURL(new Blob([response.data]));
                const fileLink = document.createElement('a');
                fileLink.href = fileURL;
                fileLink.setAttribute('download', fileName);
                document.body.appendChild(fileLink);

                fileLink.click();

                resolve(response.data);
            })
            .catch((error) => {
                Errors.handle(error);
                reject();
            })
            .finally(() => {
                context.commit('shared/deactivateLoading', 'shared/downloadFile', { root: true });
            });
        });
    },
    [FETCH_SORTING_PARAMS](context, {stateModule, sortingParams}) {
        context.commit(`${stateModule}/setSort`, sortingParams, { root: true }) ;
    },
    [SET_ACTIVE_LOADING](context, key) {
        context.commit('activateLoading', key);
    },
    [SET_INACTIVE_LOADING](context, key) {
        context.commit('deactivateLoading', key);
    },
    [GET_ORGANISATIONS_LOGO](context) {
        context.commit('shared/activateLoading', 'shared/getOrganisationsLogo', { root: true });
        return new Promise((resolve, reject) => {
            ApiService.get('/organisations/logos')
            .then((response) => {
                if (response.headers["x-file-download-access-token"]) {
                    context.commit(SET_LOGO_TOKEN, response.headers["x-file-download-access-token"]);
                }
                resolve(response.data);
            })
            .catch((error) => {
                Errors.handle(error);
                reject();
            }).finally(() => {
                context.commit('shared/deactivateLoading', 'shared/getOrganisationsLogo', { root: true });
            })
        });
    },
    async [FETCH_ORGANISATIONS_TRANSLATIONS](context) {
        const language = i18nService.getActiveLanguage();
        if (language === 'ro') {
            context.commit(SET_ORGANISATIONS_TRANSLATIONS, {});
            return;
        }

        const bundle = await TranslationsService.bundle(ORGANISATIONS_TRANSLATIONS_NAMESPACE, language);
        context.commit(SET_ORGANISATIONS_TRANSLATIONS, bundle);
    },
    async [FETCH_APPLICATION_TYPES_TRANSLATIONS](context) {
        const language = i18nService.getActiveLanguage();
        if (language === 'ro') {
            context.commit(SET_APPLICATION_TYPES_TRANSLATIONS, {});
            return;
        }

        const bundle = await TranslationsService.bundle(APPLICATION_TYPES_TRANSLATIONS_NAMESPACE, language);
        context.commit(SET_APPLICATION_TYPES_TRANSLATIONS, bundle);
    },
    async [FETCH_COMPLAIN_TYPES_TRANSLATIONS](context) {
        const language = i18nService.getActiveLanguage();
        if (language === 'ro') {
            context.commit(SET_COMPLAIN_TYPES_TRANSLATIONS, {});
            return;
        }

        const bundle = await TranslationsService.bundle(COMPLAIN_TYPES_TRANSLATIONS_NAMESPACE, language);
        context.commit(SET_COMPLAIN_TYPES_TRANSLATIONS, bundle);
    },
    async [FETCH_DISCOUNTS_TRANSLATIONS](context) {
        const language = i18nService.getActiveLanguage();
        if (language === 'ro') {
            context.commit(SET_DISCOUNTS_TRANSLATIONS, {});
            return;
        }

        const bundle = await TranslationsService.bundle(DISCOUNTS_TRANSLATIONS_NAMESPACE, language);
        context.commit(SET_DISCOUNTS_TRANSLATIONS, bundle);
    },
    async [FETCH_TAX_CATEGORIES_TRANSLATIONS](context) {
        const language = i18nService.getActiveLanguage();
        if (language === 'ro') {
            context.commit(SET_TAX_CATEGORIES_TRANSLATIONS, {});
            return;
        }

        const bundle = await TranslationsService.bundle(TAX_CATEGORIES_TRANSLATIONS_NAMESPACE, language);
        context.commit(SET_TAX_CATEGORIES_TRANSLATIONS, bundle);
    },
    [SET_ATLAS_PUBLIC_VIEW_STATE](context, atlasPublicViewstate) {
        context.commit('SET_ATLAS_PUBLIC_VIEW', atlasPublicViewstate);
    },
    [DOWNLOAD_FILE_CONTENT](context, { url, token, suppressErrorHandling }) {
        context.commit('shared/activateLoading', 'shared/downloadFileContent', { root: true });
        return new Promise((resolve, reject) => {
            ApiService.download(url, token).then((response) => {
                resolve(response.data);
            })
            .catch((error) => {
                if (!suppressErrorHandling) {
                    Errors.handle(error);
                }   
                reject();
            })
            .finally(() => {
                context.commit('shared/deactivateLoading', 'shared/downloadFileContent', { root: true });
            });
        });
    },
};

const mutations = {
    activateLoading(state, key) {
        Vue.set(state.loading, key, true);
    },
    deactivateLoading(state, key) {
        Vue.set(state.loading, key, false);
    },
    [SET_ORGANISATIONS](state, organisations) {
        state.organisations = organisations;
    },
    [SET_ORGANISATION](state, organisation) {
        state.organisation = organisation;
    },
    [CLEAR_ORGANISATION](state) {
        state.organisation = {};
    },
    [SET_USER_TYPES](state, userTypes) {
        state.userTypes = userTypes;
    },
    [SET_SETTINGS](state, settings) {
        const settingsObject = settings.reduce(
            (obj, item) => Object.assign(obj, { [item.key]: item.default_value }), {});
        state.settings = settingsObject;
    },
    // Only keys already loaded, and only strings, are overwritten - so a
    // boolean like CITIZEN_MAINTENANCE cannot be turned into a string.
    [APPLY_SETTINGS_TRANSLATIONS](state, bundle) {
        const translations = {};
        for (const [key, fields] of Object.entries(bundle || {})) {
            if (fields && fields.default_value) {
                translations[key] = fields.default_value;
            }
        }

        if (!Object.keys(translations).length) {
            return;
        }

        const overlay = (target) => {
            const next = { ...target };
            let changed = false;
            for (const key of Object.keys(next)) {
                if (translations[key] && typeof next[key] === 'string') {
                    next[key] = translations[key];
                    changed = true;
                }
            }
            return changed ? next : target;
        };

        state.settings = overlay(state.settings);
        state.publicSettings = overlay(state.publicSettings);
    },
    [SET_PUBLIC_SETTINGS](state, settings) {
        const settingsObject = settings.reduce(
            (obj, item) => Object.assign(obj, { [item.key]: item.default_value }), {});
        state.publicSettings = settingsObject;
    },
    [SET_LOGO_TOKEN](state, token) {
        state.logoToken = token;
    },
    [SET_MODULE_NAME](state, name) {
        state.moduleName = name;
    },
    [SET_ATLAS_PUBLIC_VIEW](state, isSelected) {
        state.isAtlasPublicView = isSelected;
    },
    [SET_ORGANISATIONS_TRANSLATIONS](state, bundle) {
        state.organisationsTranslations = bundle || {};
    },
    [SET_APPLICATION_TYPES_TRANSLATIONS](state, bundle) {
        state.applicationTypesTranslations = bundle || {};
    },
    [SET_COMPLAIN_TYPES_TRANSLATIONS](state, bundle) {
        state.complainTypesTranslations = bundle || {};
    },
    [SET_DISCOUNTS_TRANSLATIONS](state, bundle) {
        state.discountsTranslations = bundle || {};
    },
    [SET_TAX_CATEGORIES_TRANSLATIONS](state, bundle) {
        state.taxCategoriesTranslations = bundle || {};
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters,
};
